// SAB-1172 / SAB-1173: the news list, rendered identically for both sports.
//
// Extracted from MLBBetaPlayerDetail, which had the markup inline. MLBPlayerCardModal
// had a SECOND copy keyed differently (player_id vs player_name against the same
// table). Adding an NFL copy would have made three. There is now one.

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface PlayerNewsItem {
  id: string;
  headline: string;
  summary: string | null;
  source: string | null;
  news_type: string | null;
  published_at: string;
  detail?: Record<string, any> | null;
}

interface Props {
  sport: 'mlb' | 'nfl';
  /** NFL: nfl_players.id. MLB: players.id when known. */
  playerId?: string | null;
  /** MLB fallback, and what the legacy MLB read path keys on. */
  playerName?: string | null;
  limit?: number;
  /** Reports the loaded count so a caller can badge a tab without a second query. */
  onLoaded?: (count: number) => void;
}

/** injury reads red, trade reads primary — same vocabulary the badge dot uses. */
function dotClass(t: string | null) {
  if (t === 'injury') return 'bg-destructive';
  if (t === 'trade') return 'bg-primary';
  if (t === 'performance') return 'bg-success';
  return 'bg-muted-foreground';
}

export default function PlayerNewsList({ sport, playerId, playerName, limit = 10, onLoaded }: Props) {
  const [items, setItems] = useState<PlayerNewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (typeof (supabase as any)?.from !== 'function') { if (!cancelled) setItems([]); return; }
        const cols = 'id, headline, summary, source, news_type, published_at, detail';
        let q = supabase.from('player_news').select(cols).eq('sport', sport);

        if (sport === 'nfl') {
          if (!playerId) { if (!cancelled) setItems([]); return; }
          q = q.eq('nfl_player_id', playerId);
        } else if (playerId) {
          // ONE keying path for MLB. Prefer the id when the caller has it; the two
          // card components previously disagreed on this and read the same table
          // two different ways.
          q = q.eq('player_id', playerId);
        } else if (playerName) {
          q = q.eq('player_name', playerName);
        } else {
          if (!cancelled) setItems([]); return;
        }

        const { data } = await q.order('published_at', { ascending: false }).limit(limit);
        if (!cancelled) {
          const rows = (data as PlayerNewsItem[]) ?? [];
          setItems(rows);
          onLoaded?.(rows.length);
        }
      } catch {
        if (!cancelled) { setItems([]); onLoaded?.(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // onLoaded intentionally omitted: callers pass an inline function, and including
    // it would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport, playerId, playerName, limit]);

  if (loading) {
    return <p className="text-xs text-muted-foreground py-6 text-center">Loading news…</p>;
  }

  // An explicit empty state. The old MLB behaviour was an unconditional badge opening
  // a blank panel, which told the user nothing about whether news existed.
  if (items.length === 0) {
    return <p className="text-xs text-muted-foreground py-6 text-center">No recent news for this player.</p>;
  }

  return (
    <div className="space-y-2">
      {items.map(item => {
        const practice = item.detail?.practice_status as string | undefined;
        return (
          <Card key={item.id}>
            <CardContent className="p-3 flex gap-2.5">
              <div className={cn('mt-0.5 w-2 h-2 rounded-full flex-shrink-0', dotClass(item.news_type))} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">{item.headline}</p>
                {item.summary && item.summary !== item.headline && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.summary}</p>
                )}
                {/* Practice status rides along on the card but never affects activeness
                    — a practice flip must not re-fire the badge (SAB-1172). */}
                {practice && (
                  <p className="text-[10px] text-muted-foreground mt-1 italic">Practice: {practice}</p>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <Badge variant="outline" className="text-[9px] capitalize px-1.5 py-0 h-4">
                    {item.news_type}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">{item.source}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
